import { get } from '@/lib/api';
import { formatTimestamp } from '@/lib/constants';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import type { ActionInventorySummary } from '@uncain/shared';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

export function InventoryActionsPage() {
	const [search, setSearch] = useState('');
	const { data, isLoading, error } = useQuery<{ actions: ActionInventorySummary[] }>({
		queryKey: queryKeys.inventory.actions(),
		queryFn: () => get<{ actions: ActionInventorySummary[] }>('/inventory/actions'),
	});

	const actions = data?.actions ?? [];
	const filtered = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (term.length === 0) {
			return actions;
		}
		return actions.filter((action) =>
			[action.displayName, action.actionType, ...action.repositories, ...action.workflows].some(
				(value) => value.toLowerCase().includes(term),
			),
		);
	}, [actions, search]);

	if (isLoading) {
		return <div className="text-muted-foreground">Loading action inventory...</div>;
	}

	if (error) {
		return <div className="text-destructive">Failed to load action inventory.</div>;
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
				<div>
					<p className="text-sm uppercase tracking-[0.18em] text-muted-foreground">Inventory</p>
					<h1 className="text-2xl font-bold text-foreground">Actions</h1>
					<p className="text-sm text-muted-foreground">
						Action usage across repositories and workflows in the active organization.
					</p>
				</div>
				<div className="w-full md:w-80">
					<label className="sr-only" htmlFor="inventory-search">
						Search actions
					</label>
					<input
						id="inventory-search"
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Search actions, repos, workflows..."
						className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/40"
					/>
				</div>
			</div>

			{filtered.length === 0 ? (
				<div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
					{actions.length === 0
						? 'No actions are available in this org inventory yet.'
						: 'No actions match the current search.'}
				</div>
			) : (
				<div className="space-y-3">
					{filtered.map((action) => (
						<ActionCard key={action.id} action={action} />
					))}
				</div>
			)}
		</div>
	);
}

function ActionCard({ action }: { action: ActionInventorySummary }) {
	return (
		<Link
			to={`/inventory/actions/detail/${encodeURIComponent(action.id)}`}
			className="block rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/30"
		>
			<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div className="min-w-0 space-y-2">
					<div className="flex flex-wrap items-center gap-2">
						<h2 className="font-semibold text-foreground">{action.displayName}</h2>
						<Badge>{action.actionType}</Badge>
						{action.isVerified && <Badge tone="success">Verified</Badge>}
						{action.isCustomerOwned && <Badge tone="info">Customer owned</Badge>}
						{action.isQuarantined && <Badge tone="danger">Quarantined</Badge>}
					</div>

					<p className="text-sm text-muted-foreground">
						{action.repositoryCount} repos · {action.workflowCount} workflows · {action.usageCount}{' '}
						usages · {action.versionCount} versions
					</p>

					<div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
						{action.repositories.map((repo) => (
							<span key={repo} className="rounded bg-muted px-2 py-1">
								{repo}
							</span>
						))}
						{action.workflows.map((workflow) => (
							<span key={workflow} className="rounded bg-muted px-2 py-1">
								{workflow}
							</span>
						))}
					</div>
				</div>

				<div className="grid grid-cols-2 gap-3 text-sm lg:min-w-72">
					<Stat label="Latest version" value={action.latestVersionRef ?? 'Unavailable'} />
					<Stat
						label="Latest resolved"
						value={
							action.latestResolvedAt ? formatTimestamp(action.latestResolvedAt) : 'Unavailable'
						}
					/>
					<Stat
						label="Findings"
						value={String(action.findingCount)}
						tone={action.findingCount > 0 ? 'danger' : 'neutral'}
					/>
					<Stat label="Detail" value="Open inventory view" tone="info" />
				</div>
			</div>
		</Link>
	);
}

function Stat({
	label,
	value,
	tone = 'neutral',
}: {
	label: string;
	value: string;
	tone?: 'neutral' | 'info' | 'danger';
}) {
	return (
		<div className="rounded-lg border border-border/70 bg-background/60 p-3">
			<p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
			<p
				className={cn(
					'mt-1 text-sm font-medium',
					tone === 'info' && 'text-primary',
					tone === 'danger' && 'text-severity-critical',
					tone === 'neutral' && 'text-foreground',
				)}
			>
				{value}
			</p>
		</div>
	);
}

function Badge({
	children,
	tone = 'neutral',
}: {
	children: React.ReactNode;
	tone?: 'neutral' | 'info' | 'success' | 'danger';
}) {
	return (
		<span
			className={cn(
				'rounded-full px-2 py-0.5 text-xs font-medium',
				tone === 'neutral' && 'bg-muted text-muted-foreground',
				tone === 'info' && 'bg-primary/10 text-primary',
				tone === 'success' && 'bg-green-100 text-green-700',
				tone === 'danger' && 'bg-red-100 text-red-700',
			)}
		>
			{children}
		</span>
	);
}
